/**
 * A counter and a row of dots joined by a line.
 *
 * Extracted from the guided review so the simulation form and the golden-set
 * screen can use the same device. It renders dots; it does not know what a
 * step is. Each caller maps its own data to `RailStep`, which is what keeps
 * this from growing a union of every screen's shape.
 *
 * Filled with a tick once passed, hollow until then, and the colour says what
 * the step concluded either way. A step nobody has been on is **not** clean
 * even when it holds nothing wrong -- otherwise the rail records what the
 * system concluded rather than what a person checked.
 */

export type RailTone = 'error' | 'clean'

export interface RailStep {
  /** Stable across re-renders, so progress can key on it. */
  readonly id: string
  readonly title: string
  readonly tone: RailTone
}

export interface ProgressTrackProps {
  readonly steps: readonly RailStep[]
  readonly index: number
  readonly visited: ReadonlySet<number>
  readonly onJump: (i: number) => void
  /** The sentence above the dots, e.g. "2 de 7 revisados · 5 restantes". */
  readonly counterText: string
  readonly ariaLabel: string
}

const TONE_COLOR: Record<RailTone, string> = {
  error: 'var(--color-text-error)',
  clean: 'var(--color-text-brand)',
}

export function ProgressTrack({
  steps,
  index,
  visited,
  onJump,
  counterText,
  ariaLabel,
}: ProgressTrackProps) {
  return (
    <div className="border-stroke-weak bg-background-sunken flex shrink-0 flex-col gap-4 border-b px-5 py-3">
      <span className="text-tP text-text-weak">{counterText}</span>

      <div className="relative h-[22px]">
        {/* The line the dots sit on, behind them; each dot carries its own fill
            so it punches through. */}
        <span aria-hidden="true" className="bg-stroke-weak absolute top-2 right-2 left-2 h-0.5" />
        <div
          className="relative flex items-start justify-between"
          role="tablist"
          aria-label={ariaLabel}
        >
          {steps.map((step, i) => {
            const seen = visited.has(i) && i !== index
            const current = i === index
            const color = TONE_COLOR[step.tone]

            return (
              <button
                key={step.id}
                type="button"
                role="tab"
                aria-selected={current}
                aria-label={step.title}
                title={step.title}
                onClick={() => onJump(i)}
                className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full p-0 text-[10px] font-bold"
                style={{
                  background: seen ? color : 'var(--color-fill-white)',
                  color: seen ? 'var(--color-fill-white)' : color,
                  border: `2px solid ${color}`,
                  boxShadow: current ? '0 0 0 3px rgba(77,100,217,0.35)' : 'none',
                }}
              >
                {step.tone === 'error' ? '!' : seen ? '✓' : ''}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
